// Libraries
import { initializeApp  } from 'firebase/app';
import { getFirestore } from "firebase/firestore";
import {firebaseConfig} from './FirebaseConfig';
import { getDocs, collection, query, where, orderBy, limit, startAt, startAfter, endAt, endBefore} from 'firebase/firestore';

/**
 * Returns a document from a collection
 * 
 * @param {string} collectionId
 * @param {Array} queries
 * @param {Array} sortBy - <OPTIONAL> Sort by a field, requires an index
 * @param {Number} limitBy <OPTIONAL> Limit the query to x number of documents
 * @param {string} startAt <OPTIONAL> Used for pagination to indicate start pagination from specific item
 * @param {string} startAfter <OPTIONAL> Used for pagination to indicate the "next page" in pagination after specific item
 * @param {string} endAtDoc <OPTIONAL> Used for pagination to indicate end pagination at specific item
 * @param {string} endBeforeDoc <OPTIONAL> Used for pagination to indicate the "previous page" in pagination before specific item
 * @throws {Error} Only one of 'startAt', 'startAfter', 'endAtDoc', or 'endBeforeDoc' is provided or neither is provided.
 * ```
 * const collectionId = 'users';
 * const queries = [
 *  ['emailaddress', '==', 'mark.bennett@lendlease.com'],
 * ];
 * const sortBy = ['created', 'desc'];
 * const limitBy = 5;
 * 
 * QueryDocument(collectionId queries, sortBy, limitBy).then((documents) =>{
 * 
 *  console.log('Documents', documents);
 * 
 * }).catch((error) => {
 * 
 *  console.log('Error', error);
 * 
 * });
 * 
 * ```
 * 
 * Documentation
 * - https://firebase.google.com/docs/firestore/query-data/queries
 * - https://cloud.google.com/firestore/docs/samples/firestore-query-collection-group-filter-eq#firestore_query_collection_group_filter_eq-nodejs
 * - https://firebase.google.com/docs/firestore/query-data/query-cursors
 */

export default async function QueryDocument(collectionId, queries, sortBy, limitBy, startAtDoc, startAfterDoc, endAtDoc, endBeforeDoc){

  try {

    //Firestore database reference
    const app = initializeApp(firebaseConfig);
    const db = getFirestore(app);
    
    //Set collection
    const collectionRef = collection(db, collectionId);

    //------------------------------------------------------
    //  Prepare query arguments 
    //------------------------------------------------------

    //Temp array used to store query arguments
    var args = []

    //Extract list of queries from array
    if(queries.length > 0){
      queries.forEach((query) => {
        const queryArg = where(query[0], query[1], query[2])
        args.push(queryArg)
      })
    }

    //Check if sortBy exists > add to query arguments
    if(sortBy?.length > 0) {
      const orderByArg = orderBy(sortBy[0], sortBy[1])
      args.push(orderByArg)
    }

    //Check if limitBy exists > add to query arguments
    if(limitBy) {
      const limitArg = limit(limitBy)
      args.push(limitArg)
    } 

    //Check if startAfterDoc exists > add to query arguments
    if (startAfterDoc?.length > 0) {
      const startAfterArg = startAfter(startAfterDoc)
      args.push(startAfterArg)
    } 

    //Check if startAtDoc exists > add to query arguments
    if (startAtDoc?.length > 0) {
      const startAtArg = startAt(startAtDoc)
      args.push(startAtArg)
    }

    //Check if startAfterDoc exists > add to query arguments
    if (endBeforeDoc?.length > 0) {
      const endBeforeArg = endBefore(endBeforeDoc)
      args.push(endBeforeArg)
    } 

    //Check if startAtDoc exists > add to query arguments
    if (endAtDoc?.length > 0) {
      const endAtArg = endAt(endAtDoc)
      args.push(endAtArg)
    }

    //------------------------------------------------------
    //  Execute query 
    //------------------------------------------------------

    //Create query
    const q = query(collectionRef, ...args);

    //Temp array used to store results 
    var results = []

    //Execute query
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      results.push(doc.data());
    });

    return results

  } 
  catch (error) {
    throw new Error(`Function QueryDocument failed to complete, error ${error}`)
  }

  //------------------------------------------------------
}